import { PropsWithChildren } from 'react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { isProduction } from 'src/config';
import authSelectors from 'src/redux/auth/auth-selectors';
import { StoreState } from 'src/redux/store';
import { AnalyticsEvent } from 'src/types/analytics';

// TYPES

type AnalyticsContextValues = {
  analyticsEvent: (props: AnalyticsEvent) => void;
};

// CONFIG

const AnalyticsContext = React.createContext<AnalyticsContextValues | null>(
  null
);

const disableTracking = !isProduction;

// HOOKS

export const useAnalyticsContext = () => {
  const value = React.useContext(AnalyticsContext);
  if (!value) {
    throw new Error(
      'component must be wrapped with <AnalyticsContext.Provider>'
    );
  }
  return value;
};

// PROVIDER
export const AnalyticsProvider = (props: PropsWithChildren<any>) => {
  const location = useLocation();
  const firstUpdate = React.useRef(true);
  const userId = useSelector(
    (state) => authSelectors.getUser(state as StoreState).id
  );
  const userConsentToCookies = useSelector((state) =>
    authSelectors.getUserCookieChoice(state as StoreState)
  );

  React.useEffect(() => {
    const paq = (window._paq = window._paq || []);
    if (disableTracking || !userConsentToCookies) {
      paq.push(['deleteCookies']);
      return;
    }

    // paq.push(['setCookieDomain', '*.cortico.ai']);
    paq.push(['setUserId', userId]);
    paq.push(['trackPageView']);
    paq.push(['enableLinkTracking']);
    paq.push(['MediaAnalytics.enableTrackEvents']);

    paq.push(['setTrackerUrl', 'https://fora.matomo.cloud/matomo.php']);
    paq.push(['setSiteId', 1]);
    const d = document,
      g = d.createElement('script'),
      s = d.getElementsByTagName('script')[0];
    if (!s.parentNode) {
      return;
    }
    g.async = true;
    g.src = '//cdn.matomo.cloud/fora.matomo.cloud/matomo.js';
    s.parentNode.insertBefore(g, s);
  }, [userConsentToCookies]);

  React.useEffect(() => {
    if (disableTracking || !userConsentToCookies) {
      if (window._paq) {
        window._paq.push(['deleteCookies']);
      }
      return;
    }

    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    if (window._paq) {
      window._paq.push(['setCustomUrl', window.location.href]);
      window._paq.push(['setDocumentTitle', document.title]);
      window._paq.push(['trackPageView']);
    }
  }, [location]);

  const analyticsEvent = ({ category, action, name }: AnalyticsEvent) => {
    if (disableTracking || !userConsentToCookies) {
      if (window._paq) {
        window._paq.push(['deleteCookies']);
      }
      return;
    }
    const paq = (window._paq = window._paq || []);
    paq.push(['setUserId', userId]);
    paq.push(['trackEvent', category, action, name]);
  };

  return (
    <AnalyticsContext.Provider value={{ analyticsEvent }}>
      {props.children}
    </AnalyticsContext.Provider>
  );
};
